import { type Country, type DestinationPrices, type Resort } from '@travelata-libs/travelata-frontend';
import DepartureCitiesRepository from "@/js/common/Domain/Repository/DepartureCities";
import CountriesRepository from '@/js/common/Domain/Repository/Countries';
import ResortsRepository from '@/js/common/Domain/Repository/Resorts';
import ResortFactory from '@/js/common/Domain/Factory/Resort';
import HotelsRepository from '@/js/common/Domain/Repository/Hotels';
import Vue, { reactive } from 'vue';
import type { Store } from 'vuex';
import Backbone from 'backbone';
import getStore from '@ve/services/getStore/getStore';
import { getSsrMountTarget } from '@shared/lib';

import type { HotelPageFlow } from '../config/hotelPageFlow';
import type { SerpVertical } from '../model/types';
import LibSearchFormHost from '../ui/LibSearchFormHost.vue';
import { createHeaderBindings } from './headerBindings';
import { readSeoAnchorProps } from './readSeoAnchorProps';
import { trackSearchFormGoal } from './trackSearchFormGoal';
import { IMapForSearchFormState } from '@fr/vue/pages/serp/store';

const HOST_ANCHOR_SELECTOR = '#js-lib-search-form-host';

interface SearchControllerLike {
    model: Backbone.Model;
    formSubmit(): void;
}

export interface MountLibSearchFormOptions {
    store?: Store<unknown>;
    controller: SearchControllerLike;
    vertical?: SerpVertical;
    flow?: HotelPageFlow;
    isStayCalendar?: boolean;
}

export interface MountLibSearchFormResult {
    isStayCalendar: boolean;
    unmount(): void;
}

interface LibSearchFormHostExpose {
    validate(): boolean;
}

export function mountLibSearchForm(options: MountLibSearchFormOptions): MountLibSearchFormResult {
    const { controller } = options;
    const store = options.store ?? getStore();
    const anchor = typeof document === 'undefined' ? null : document.querySelector(HOST_ANCHOR_SELECTOR);
    const header = createHeaderBindings();
    const seoProps: Record<string, unknown> = options.vertical === 'seo'
        ? { ...readSeoAnchorProps(anchor), cities: header.props.cities, currentCity: header.props.currentCity }
        : {};
    const isHotelsSearch = options.vertical === 'hotels' || seoProps.vertical === 'hotels';
    const isStayCalendar = Boolean(options.isStayCalendar)
        && (isHotelsSearch || (options.vertical === 'hotelPage' && options.flow === 'hotelOffer'));
    const priceState = reactive<{ prices: DestinationPrices }>({
        prices: { country: {}, resort: {} },
    });

    const onMinPricesLoaded = ({ countries, resorts }: { countries: Backbone.Model<Country>[], resorts: Backbone.Model<Resort>[] }) => {
        const country: Record<number, number> = { ...priceState.prices.country };
        const resort: Record<number, number> = { ...priceState.prices.resort };
        countries.forEach((model) => {
            country[model.get('id')] = model.get('minPrice') || 0;
        });
        resorts.forEach((model) => {
            resort[model.get('id')] = model.get('minPrice') || 0;
        });
        priceState.prices = { country, resort };
    };

    const rootVm = new Vue({
        name: 'LibSearchFormHostRoot',
        store,
        render: (createElement) => createElement(LibSearchFormHost, {
            ref: 'host',
            props: {
                authData: { user: header.auth.user, accountUrl: header.props.accountUrl },
                prices: priceState.prices,
                vertical: options.vertical,
                flow: options.flow,
                seoProps,
            },
            on: {
                ...header.listeners,
                search: () => onSearch(),
                track: trackSearchFormGoal,

                ['update:modelValue']: (data: IMapForSearchFormState) => {
                    const departureCity = DepartureCitiesRepository.repository.get(data.departureCity.id);
                    let country = CountriesRepository.repository.get(data.countries[0].id);
                    const modelsHotel = HotelsRepository.repository.getHotelsBuild(data.hotels);
                    let resorts = [];
                    if(data.resorts.length) {
                        resorts = data.resorts.map((r) => new ResortFactory({ ...r, country: data.countries[0].id }));
                    }
                    if (data.countries.length && data.countries[0]?.resorts?.length) {
                        ResortsRepository.repository.addCountryResorts(data.countries[0].resorts, data.countries[0].id);
                    }
                    country = CountriesRepository.repository.get(data.countries[0].id);
                    controller.model.get('countries').set('model', new CountriesRepository([country.clone()]), { disabledSetResorts: true });
                    controller.model.get('resorts').set('model', new Backbone.Collection(resorts));
                    controller.model.get('hotels').set('model', modelsHotel);
                    controller.model.get('departureCity').set('model', departureCity);
                    controller.model.get('nightRange').set('model', new Backbone.Model(data.nightRange));
                    controller.model.get('touristGroup').set('model', new Backbone.Model(data.touristGroup));
                    controller.model.get('checkInDateRange').set('model', new Backbone.Model(data.checkInDateRange));
                },
            },
        }),
    });

    function getHost(): LibSearchFormHostExpose | null {
        return (rootVm.$refs.host as unknown as LibSearchFormHostExpose | undefined) ?? null;
    }

    function onSearch(): void {
        const host = getHost();
        const isFormValid = host ? host.validate() : true;
        if (!isFormValid) {
            return;
        }
        controller.formSubmit();
    }
    if (anchor) {
        CountriesRepository.repository.on('minPricesLoaded', onMinPricesLoaded);
        const mountTarget = getSsrMountTarget(anchor);
        // Режим страницы отеля лежит в хеше адреса, SSR его не знает и рисует поле ночей. Гидрация оставила бы
        // эту ячейку на странице, поэтому без признака SSR Vue отрисует форму заново на месте разметки.
        const isSsrMarkupStale = isStayCalendar && options.vertical === 'hotelPage';
        if (isSsrMarkupStale && mountTarget instanceof HTMLElement) {
            delete mountTarget.dataset.serverRendered;
        }
        rootVm.$mount(mountTarget);
    }

    return {
        isStayCalendar: isStayCalendar && Boolean(anchor),
        unmount(): void {
            CountriesRepository.repository.off('minPricesLoaded', onMinPricesLoaded);
            header.destroy();
            rootVm.$destroy();
        },
    };
}
